import { computed, type Ref } from 'vue'
import type { GridCol } from '~~/types/orgGrid'
import { useIsOrganigrammeEditing } from '~/composables/auth/useOrganigrammeEditRouteAccess'
import { useOrgEditActor } from '~/composables/auth/useOrgEditActor'
import { useDomaines } from '~/composables/orga/useDomaine'
import { useOrgGridReorder } from '~/composables/orga/reorder/useOrgGridReorder'
import type { ReorderGroup } from '~/composables/orga/reorder/reorderModel'
import { actorMayReorderDomaines } from '~~/utils/auth/orgEditAffordances'

function columnKey(column: GridCol): string {
  return column.header.label ?? ''
}

function writeDomaineColumns(grid: Ref<GridCol[]>, groups: ReorderGroup[]): void {
  const group = groups[0]
  if (!group) {
    return
  }
  const byLabel = new Map(grid.value.map(function indexColumn(column) {
    return [columnKey(column), column] as const
  }))
  const ordered = group.keys
    .map(function columnForKey(key) {
      return byLabel.get(key)
    })
    .filter(function keepColumn(column): column is GridCol {
      return column !== undefined
    })
  const omitted = grid.value.filter(function notListed(column) {
    return !group.keys.includes(columnKey(column))
  })
  grid.value = [...ordered, ...omitted]
}

export function useDirectionDomaineReorder(
  grid: Ref<GridCol[]>,
  directionLib: Ref<string>,
  reload: () => Promise<void>,
) {
  const isEditing = useIsOrganigrammeEditing()
  const { actor } = useOrgEditActor()
  const { reorderDomaines } = useDomaines()
  const canReorder = computed(
    () => isEditing.value && actorMayReorderDomaines(actor.value, directionLib.value),
  )

  return useOrgGridReorder({
    axis: 'horizontal',
    enabled: canReorder,
    isEditing,
    readGroups: function readDomaines() {
      return [
        {
          id: directionLib.value,
          keys: grid.value.map(columnKey).filter(function keepKey(key) {
            return key !== ''
          }),
        },
      ]
    },
    writeGroups: (groups) => writeDomaineColumns(grid, groups),
    persist: async function persistDomaines(groups) {
      const group = groups[0]
      if (group && group.keys.length > 0) {
        await reorderDomaines(group.id, group.keys)
      }
    },
    reload,
    successMessage: 'Ordre des domaines enregistré',
  })
}
